<template>
<div>
  <loading-panel :loading="loading"></loading-panel>

  <div class="row">
    <div class="col">
      <card>
        <tab-rail :tabs="tabs" v-model="activeTabIndex" tight>
          <template #actions>
            <base-button v-if="activeTabIndex === 0" size="sm" type="primary" @click="createNewOverlay">
              <i class="tim-icons icon-simple-add mr-1"></i> Create overlay
            </base-button>
          </template>
        </tab-rail>

        <!-- Overlays tab: the endpoint returns every overlay of the template, so
             filters and paging are client side. -->
        <div v-if="activeTabIndex === 0">
          <filter-bar
            class="mt-3"
            :filters="filterSchema"
            :initial-value="filterValues"
            :loading="loading"
            :result-count="filteredOverlays.length"
            :per-page="perPage"
            saved-filters-key="photo-overlays"
            mobile-collapsible
            :saved-filters-entity="savedViewsEntity"
            @change="onFiltersChanged"
            @update:perPage="onPerPageChange"
          />

          <data-table
            class="mt-3"
            :columns="columns"
            :rows="pagedOverlays"
            row-key="id"
            :loading="loading"
            :page="page"
            :per-page="perPage"
            :total="filteredOverlays.length"
            :sort-value="sort"
            :default-sort="{ key: 'name', dir: 'asc' }"
            clickable
            @update:page="page = $event"
            @update:sort="sort = $event"
            @row-click="editOverlay"
          >
            <template #cell(name)="{ row }">
              <div class="data-table__primary">{{ row.name }}</div>
              <div v-if="row.description" class="data-table__muted">{{ row.description }}</div>
            </template>
            <template #cell(aspect_ratio)="{ row }">
              <span class="badge-tag badge-tag--primary">{{ row.aspect_ratio }}</span>
            </template>
            <template #cell(applied_by_default)="{ row }">
              <template v-if="Number(row.applied_by_default) === 1">
                <div>{{ automationLabel(row) }}</div>
                <el-popover v-if="Number(row.events_count) > 0" trigger="hover" placement="top-start" width="260">
                  <div class="popover-body" style="word-break: normal; text-align: left">{{ row.event_names }}</div>
                  <span slot="reference" class="data-table__muted po-events">{{ eventsLabel(row) }}</span>
                </el-popover>
              </template>
              <span v-else class="data-table__muted">Manual</span>
            </template>
            <template #actions="{ row }">
              <el-dropdown>
                <base-button simple type="primary" link size="sm">
                  <i class="tim-icons el-icon-more"></i>
                </base-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <a class="dropdown-item mt-1 mb-2" href="#" @click.prevent="editOverlay(row)">
                      <i class="tim-icons text-default icon-pencil mr-2"></i> Edit overlay
                    </a>
                    <hr>
                    <a class="dropdown-item" href="#" @click.prevent="deleteOverlay(row)">
                      <i class="tim-icons text-default icon-trash-simple mr-2"></i> Delete overlay
                    </a>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </template>
            <template #empty>
              <img src="/img/pandas/photo.png" alt="" class="empty-state-img empty-state-img--md" />
              <h4 class="mt-3 empty-state-text">{{ emptyState.title }}</h4>
              <p class="data-table__muted">{{ emptyState.text }}</p>
            </template>
          </data-table>
        </div>

        <!-- Settings tab -->
        <div v-if="activeTabIndex === 1" class="mt-3">
          <collapsible-section title="Taking photos" icon="tim-icons icon-camera-18" :collapsible="false">
            <div class="row">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="review_before_save"
                           :disabled="saving" />
                <label class="text-white"> &nbsp; Review photos before saving
                  <el-popover trigger="hover"
                              placement="right"
                              width="300">
                    <div>
                      <div class="popover-body" style="word-break: normal; text-align: left">When on, every photo taken or uploaded opens with its overlay before it is saved. Staff can move or hide overlay layers, retake the photo, or accept it. Off means photos save as soon as they are taken.</div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
            <div class="row mt-1">
              <div class="col">
                <el-switch active-color="#e14eca"
                           v-model="confirm_before_delete"
                           :disabled="saving" />
                <label class="text-white"> &nbsp; Require confirmation before deleting a photo
                  <el-popover trigger="hover"
                              placement="right"
                              width="300">
                    <div>
                      <div class="popover-body" style="word-break: normal; text-align: left">When on, staff are asked to confirm before a team photo is deleted. This helps prevent accidental deletions on touch devices.</div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
              </div>
            </div>
            <div class="row mt-3">
              <div class="col">
                <label class="text-white d-block mb-2">Camera
                  <el-popover trigger="hover"
                              placement="right"
                              width="300">
                    <div>
                      <div class="popover-body" style="word-break: normal; text-align: left">System camera opens the device's own camera or photo picker. Live overlay camera opens a camera view inside the console with the overlay drawn over the live picture, so staff can frame the group around it. Devices that cannot share their camera with the browser use the system camera either way, and staff can switch to the system camera for a single shot from inside the live view.</div>
                    </div>
                    <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
                  </el-popover>
                </label>
                <segmented-toggle v-model="camera_mode" variant="pills" size="sm" :disabled="saving"
                                  :options="[{ value: 'native', label: 'System camera' }, { value: 'live', label: 'Live overlay camera' }]" />
              </div>
            </div>
          </collapsible-section>
          <div class="mt-3">
            <base-button type="primary" size="sm" :disabled="saving || loading" @click="saveSettings">
              {{ saving ? 'Saving...' : 'Save' }}
            </base-button>
          </div>
        </div>
      </card>
    </div>
  </div>

  <base-alert-dialog :show="alertDialog.show"
                     :title="alertDialog.title"
                     :message="alertDialog.message"
                     :variant="alertDialog.variant"
                     :show-cancel="alertDialog.showCancel"
                     :confirm-text="alertDialog.confirmText"
                     :cancel-text="alertDialog.cancelText"
                     :confirm-variant="alertDialog.confirmVariant"
                     @confirm="resolveAlert(true)"
                     @cancel="resolveAlert(false)" />
</div>
</template>

<script>
import { Auth } from 'aws-amplify'
import { API_LOCATION } from '@/api-config'
import axios from 'axios'
// el-dropdown is not globally registered (see globalComponents.js); register locally.
import { Dropdown, DropdownMenu, Switch, Popover } from 'element-ui'
import TabRail from '@/components/TabRail.vue'
import CollapsibleSection from '@/pages/Bookings/BookinSiteSettings/components/shared/CollapsibleSection'

const CONDITION_LABELS = {
  escaped: 'the event was completed',
  not_escaped: 'the event was not completed',
}

export default {
  name: 'PhotoSettingsTemplate',
  components: {
    TabRail,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [Switch.name]: Switch,
    [Popover.name]: Popover,
    CollapsibleSection,
  },
  data() {
    return {
      loading: false,
      saving: false,
      overlays: [],
      confirm_before_delete: false,
      review_before_save: false,
      camera_mode: 'native',
      template_name: '',
      default_aspect_ratio: '',
      activeTabIndex: 0,
      columns: [
        { key: 'name', label: 'Overlay', sortable: true },
        { key: 'aspect_ratio', label: 'Aspect ratio', sortable: true },
        { key: 'applied_by_default', label: 'Automation', sortable: true },
      ],
      filterValues: { name: '', aspect_ratio: '', automation: '' },
      sort: { key: 'name', dir: 'asc' },
      page: 1,
      perPage: 10,
      tabs: [
        { name: 'Overlays' },
        { name: 'Settings' },
      ],
      alertDialog: {
        show: false, title: '', message: '', variant: 'warning', showCancel: false,
        confirmText: 'OK', cancelText: 'Cancel', confirmVariant: 'primary',
      },
    }
  },
  created() {
    this.alertResolve = null
  },
  computed: {
    savedViewsEntity() {
      return { id: this.$route.query.id, name: this.template_name || undefined }
    },
    filterSchema() {
      const ratios = [...new Set(this.overlays.map(o => o.aspect_ratio).filter(Boolean))]
      return [
        { key: 'name', type: 'text', label: 'Name', placeholder: 'Overlay name', operatorLabel: 'contains', primary: true },
        { key: 'aspect_ratio', type: 'select', label: 'Aspect ratio', primary: true, options: ratios.map(r => ({ value: r, label: r })) },
        { key: 'automation', type: 'select', label: 'Automation', options: [
          { value: 'auto', label: 'Applies automatically' },
          { value: 'manual', label: 'Manual' },
        ] },
      ]
    },
    hasActiveFilters() {
      return !!(this.filterValues.name || this.filterValues.aspect_ratio || this.filterValues.automation)
    },
    filteredOverlays() {
      const needle = String(this.filterValues.name || '').trim().toLowerCase()
      const ratio = this.filterValues.aspect_ratio
      const automation = this.filterValues.automation
      const rows = this.overlays.filter(o => {
        if (needle && !String(o.name || '').toLowerCase().includes(needle)) return false
        if (ratio && o.aspect_ratio !== ratio) return false
        if (automation === 'auto' && Number(o.applied_by_default) !== 1) return false
        if (automation === 'manual' && Number(o.applied_by_default) === 1) return false
        return true
      })
      const { key, dir } = this.sort || { key: 'name', dir: 'asc' }
      const sign = dir === 'desc' ? -1 : 1
      return [...rows].sort((a, b) => {
        const av = key === 'applied_by_default' ? Number(a[key]) : String(a[key] || '').toLowerCase()
        const bv = key === 'applied_by_default' ? Number(b[key]) : String(b[key] || '').toLowerCase()
        if (av < bv) return -1 * sign
        if (av > bv) return 1 * sign
        return 0
      })
    },
    pagedOverlays() {
      const start = (this.page - 1) * this.perPage
      return this.filteredOverlays.slice(start, start + this.perPage)
    },
    emptyState() {
      if (this.hasActiveFilters) {
        return { title: 'No overlays match these filters', text: 'Try a different name or clear the filters.' }
      }
      return { title: 'No overlays created yet', text: 'Overlays add your logo, the event name, the team name or the finish time to every photo.' }
    },
  },
  watch: {
    filteredOverlays(rows) {
      const last = Math.max(1, Math.ceil(rows.length / this.perPage))
      if (this.page > last) this.page = last
    },
  },
  methods: {
    // "Always applies automatically" or "Applies automatically when the event was not completed".
    automationLabel(row) {
      const when = CONDITION_LABELS[row.apply_condition]
      if (!when || row.apply_condition === 'always') return 'Always applies automatically'
      return 'Applies automatically when ' + when
    },
    // "3 events", with the names in a hover popover.
    eventsLabel(row) {
      const count = Number(row.events_count) || 0
      return count === 1 ? '1 event' : count + ' events'
    },
    onFiltersChanged(values) {
      this.filterValues = { ...this.filterValues, ...values }
      this.page = 1
    },
    onPerPageChange(value) {
      this.perPage = value
      this.page = 1
    },
    getAuthToken() {
      return new Promise(resolve => {
        Auth.currentSession()
          .then(data => resolve(data.idToken.jwtToken))
          .catch(err => console.log(err))
      })
    },
    async getTemplate() {
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const url = API_LOCATION + 'companies/' + companyId + '/photo-settings/' + this.$route.query.id
        const response = await axios.get(url, config)
        this.overlays = response.data.overlays || []
        const settings = response.data.photo_settings || {}
        this.confirm_before_delete = !!settings.confirm_before_delete
        this.review_before_save = !!settings.review_before_save
        this.camera_mode = settings.camera_mode === 'live' ? 'live' : 'native'
        this.template_name = settings.name || ''
        this.default_aspect_ratio = settings.default_aspect_ratio
      } catch (err) {
        this.handleApiError(err)
      } finally {
        this.loading = false
      }
    },
    async saveSettings() {
      this.saving = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const url = API_LOCATION + 'companies/' + companyId + '/photo-settings/' + this.$route.query.id
        await axios.put(url, {
          confirm_before_delete: this.confirm_before_delete ? 1 : 0,
          review_before_save: this.review_before_save ? 1 : 0,
          camera_mode: this.camera_mode
        }, config)
        this.$notify({ type: 'success', message: 'Photo settings updated', icon: 'tim-icons icon-check-2' })
      } catch (err) {
        this.handleApiError(err)
      } finally {
        this.saving = false
      }
    },
    createNewOverlay() {
      this.$router.push({ path: '/photos/overlays', query: { id: this.$route.query.id } })
    },
    editOverlay(item) {
      this.$router.push({ path: '/photos/overlays', query: { id: this.$route.query.id, oid: item.id } })
    },
    async deleteOverlay(item) {
      const ok = await this.showAlert({
        title: 'Delete overlay?',
        message: 'Photos that already have this overlay applied keep it. New photos will not be able to use it.',
        variant: 'warning',
        showCancel: true,
        confirmText: 'Delete',
        confirmVariant: 'danger',
      })
      if (!ok) return
      this.loading = true
      try {
        const token = await this.getAuthToken()
        const config = { headers: { Authorization: token } }
        const companyId = localStorage.getItem('companyId')
        const url = API_LOCATION + 'companies/' + companyId + '/photo-settings/' + this.$route.query.id + '/overlays/' + item.id
        await axios.delete(url, config)
        this.$notify({ type: 'success', message: 'Overlay deleted', icon: 'tim-icons icon-check-2' })
        await this.getTemplate()
      } catch (err) {
        this.loading = false
        this.handleApiError(err)
      }
    },
    // Promise-based wrapper around the shared base-alert-dialog. Resolves true
    // on confirm, false on cancel or dismiss.
    showAlert(opts) {
      if (this.alertResolve) this.alertResolve(false)
      this.alertDialog = {
        show: true,
        title: opts.title || '',
        message: opts.message || '',
        variant: opts.variant || 'warning',
        showCancel: opts.showCancel === true,
        confirmText: opts.confirmText || 'OK',
        cancelText: opts.cancelText || 'Cancel',
        confirmVariant: opts.confirmVariant || 'primary',
      }
      return new Promise(resolve => { this.alertResolve = resolve })
    },
    resolveAlert(ok) {
      this.alertDialog.show = false
      const resolve = this.alertResolve
      this.alertResolve = null
      if (resolve) resolve(ok)
    },
    async handleApiError(err) {
      console.error(err)
      const status = err && err.response && err.response.status
      if (status === 401) {
        await this.showAlert({
          title: 'Error',
          message: 'You do not have access to this feature. Reach out to your admin regarding your site permissions.',
          variant: 'danger',
        })
        this.$router.push('/')
        return
      }
      this.showAlert({ title: 'Error', message: 'Something went wrong', variant: 'danger' })
    },
  },
  mounted() {
    this.getTemplate()
  },
}
</script>

<style scoped>
.po-events { cursor: default; border-bottom: 1px dotted rgba(125, 125, 125, 0.6); }
</style>
